import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { GTMService } from '@services';
import { DevicesStore, InstallationFeaturesStore } from '@store';
import {
  DeviceNameCommands,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  IsStringValueProperty,
  SetNumberValueCommands,
} from '@vi-iot/client/index';
import { Observable, Subject, takeUntil } from 'rxjs';
import { EditDeviceNameModalComponent } from 'src/app/+installations/single-room-control/edit-device-name-modal/edit-device-name-modal.component';
import { SharedDependenciesModule } from 'src/app/shared';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';

@Component({
  selector: 'app-dry-contact-generic-feature-overlay',
  standalone: true,
  imports: [CommonStandaloneModule, SharedDependenciesModule],
  templateUrl: './dry-contact-generic-feature-overlay.component.html',
  styleUrl: './dry-contact-generic-feature-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DryContactGenericFeatureOverlayComponent implements OnInit, OnDestroy {
  readonly FeaturePropertyKey = FeaturePropertyKey;
  readonly FeatureCommands = FeatureCommands;
  deviceName$: Observable<Feature<IsStringValueProperty, DeviceNameCommands>>;
  powerNominal$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  deviceNameFallback = '';

  private deviceId: string;
  private ngUnsubscribe$: Subject<void> = new Subject();

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private gtmService: GTMService,
    private dialog: MatDialog,
    private devicesStore: DevicesStore,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.devicesStore.selectedDevice$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((device) => {
      this.deviceId = device.id;
      this.deviceNameFallback = this.getFallbackDeviceName(device.id);
      this.setFeatureObservables();
    });
  }

  private setFeatureObservables(): void {
    this.deviceName$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceName,
      this.deviceId
    );

    this.powerNominal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dryContactPowerNominal,
      this.deviceId
    );
  }

  private getFallbackDeviceName(deviceId: string): string {
    // Extract number from device ID (e.g., "hems-2" -> "2")
    const match = deviceId.match(/-?(\d+)$/);
    const deviceNumber = match ? match[1] : '';
    const otherDeviceTranslation = this.translate.instant(
      '+INSTALLATIONS.DRY_CONTACTS.GENERIC_CONSUMER.OTHER_DEVICE'
    );

    return `${otherDeviceTranslation} ${deviceNumber}`;
  }

  openEditModal(): void {
    this.gtmService.track('onOpen', 'Edit Device Name modal', 'dryContactGeneric');
    this.dialog.open(EditDeviceNameModalComponent, {
      width: '715px',
      panelClass: 'edit-device-name-modal',
      data: {
        deviceId: this.deviceId,
      },
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
